import { registerCopyGameReturn, saveCopyGameDraft, readCopyGameDraft, clearCopyGameDraft, type CopyGameDraft } from '@src/utils/copyGameReturn';
import { PremiumFeatureBadge } from '@src/components/PremiumBadge/PremiumFeatureBadge';
import { PremiumUpsell } from '@src/components/PremiumUpsell/PremiumUpsell';
import { trackGameCopy, type GameCopySource } from '@src/utils/gameCopyAnalytics';
import { useAppSelector } from '@src/state/hooks';
import { ACCOUNT_PATH } from '@src/routing/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { premiumGameUrl } from '@src/routing/boxDomainNavigation';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BoxTheme,
  hasPremiumFeature,
  type BoxInfo,
  type CustomField,
  normalizeQuickBoxName,
  type CardInfo,
  type CashCurrency,
} from '@lub0v/santa-utils';
import {
  Button,
  Switch,
  showDialog,
  FileUpload,
  IMAGE_UPLOAD_MAX_SIZE,
  Hint,
  PicturePicker,
  Spinner,
  Stack,
  Typography,
  type FileUploadHandle,
  type NamesProblem,
} from '@src/components';
import { santaClient } from '@src/state/client';
import { closeDialog } from '@src/components/Dialog/Dialog';
import { NewBoxReview } from '@src/pages/NewBoxPage/NewBoxReview';
import { AppearancePicker } from '@src/pages/NewBoxPage/AppearancePicker';
import { emptyDraft, toCreateInput, type NewBoxDraft } from '@src/pages/NewBoxPage/draft';
import { defaultCashCurrency } from '@src/site/currency';
import './DuplicateGameDialog.css';

export function useDuplicateGameDialog(source: GameCopySource) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isGuest = useAppSelector((state) => state.session.user?.isGuest);
  return (item: BoxInfo) => {
    trackGameCopy('game_copy_clicked', source, isGuest ? 'guest' : 'account');
    if (isGuest) {
      showDialog({
        title: t('game.settings.copyGuestTitle'),
        children: t('game.settings.copyGuestBody'),
        cancel: t('box.cancel'),
        submit: t('game.settings.copyGuestAction'),
        testId: 'duplicate-game-guest-dialog',
        onSubmit: () => { navigate(ACCOUNT_PATH); },
      });
      return;
    }
    showDialog({
      title: t('boxes.duplicate.title'),
      mobileFullScreen: true,
      preventDismiss: true,
      hideClose: true,
      testId: 'duplicate-game-dialog',
      children: <DuplicateGameDialog item={item} source={source} />,
    });
  };
}

export function DuplicateGameDialog({ item, source, restored }: { item: BoxInfo; source: GameCopySource; restored?: CopyGameDraft }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<NewBoxDraft | null>(null);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [excludePreviousGiftees, setExcludePreviousGiftees] = useState(restored?.excludePreviousGiftees ?? true);
  const user = useAppSelector((state) => state.session.user);
  const canCopyExclusions = Boolean(user && hasPremiumFeature(user, 'boxDraw'));
  const trackedShown = useRef(false);
  useEffect(() => {
    if (trackedShown.current) return;
    trackedShown.current = true;
    trackGameCopy('game_copy_modal_shown', source, user?.isGuest ? 'guest' : 'account');
  }, [source, user?.isGuest]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(true);
  const [editedQuestions, setEditedQuestions] = useState<CustomField[]>([]);
  const originalCards = useRef<CardInfo[]>([]);
  const newKey = useRef(restored?.newKey ?? '');
  const submitting = useRef(false);
  const [questions, setQuestions] = useState<CustomField[]>([]);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [namesProblem, setNamesProblem] = useState<NamesProblem | null>(null);
  const [editingTheme, setEditingTheme] = useState(restored?.editingTheme ?? false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const upload = useRef<FileUploadHandle>(null);
  useEffect(() => registerCopyGameReturn(() => {
    if (!draft || !user) return null;
    return saveCopyGameDraft({ userId: user.id, sourceKey: item.box.key, source, draft,
      questions: editedQuestions, excludePreviousGiftees, newKey: newKey.current,
      editingTheme, at: Date.now() });
  }), [draft, user, item.box.key, source, editedQuestions, excludePreviousGiftees, editingTheme]);
  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- A newly selected source game starts a fresh external duplication request.
    setError(false);
    setDraft(null);
    void Promise.all([
      santaClient.getBoxInfo({ key: item.box.key }),
      santaClient.getCards({ key: item.box.key }),
      santaClient.getQuestions({ key: item.box.key }),
    ])
      .then(([info, cards, fields]) => {
        if (cancelled) return;
        if (
          !info.ok ||
          !info.data?.isAdmin ||
          !cards.ok ||
          !cards.data ||
          !fields.ok ||
          !fields.data
        )
          throw new Error('Could not load game');
        const source = info.data;
        const box = source.box;
        const originalName = box.name.trim();
        const currentYear = String(new Date().getFullYear());
        const yearPattern = /(?<!\d)[12]\d{3}(?!\d)/g;
        const copyName = yearPattern.test(originalName)
          ? originalName.replace(yearPattern, currentYear)
          : `${originalName} ${currentYear}`;
        const currency = (box.cashLimitCurrency || defaultCashCurrency()) as CashCurrency;
        const delivery = fields.data.find(
          (field: CustomField) => field.type === 'delivery_address',
        );
        setQuestions(restored?.questions ?? fields.data);
        setEditedQuestions(restored?.questions ?? fields.data);
        originalCards.current = cards.data;
        setDraft(restored ? { ...restored.draft, features: user?.features, isPremium: user?.isPremium === true } : {
          ...emptyDraft(copyName, currency),
          isPremium: user?.isPremium === true,
          features: user?.features,
          theme: box.theme || BoxTheme.HALLOWEEN,
          picture: box.picture,
          logo: box.logo || null,
          useNames: box.useNames,
          usePost: box.usePost,
          isPhoneRequired: box.isPhoneRequired,
          useCashLimit: box.useCashLimit,
          cashLimit: box.cashLimit || 0,
          cashLimitMin: box.cashLimitMin || 0,
          budgetPreset: box.useCashLimit ? 'custom' : 'none',
          exchange: delivery?.enabled
            ? delivery.config.deliveryMode === 'pickup'
              ? 'pickup'
              : 'send'
            : 'meet',
          useWish: box.useWish,
          participates: source.isParticipant,
          organizerName: source.cardUsername || source.admin.username || '',
          participantNames: cards.data.filter((card) => !card.isYou).map((card) => card.username),
        });
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [item.box.key, attempt]);

  async function submit() {
    if (!draft || submitting.current || blocked || namesProblem) return;
    submitting.current = true;
    setBusy(true);
    setSaveError(null);
    try {
      if (!newKey.current) {
        const generated = await santaClient.generateBoxKey();
        if (!generated.ok || !generated.data) throw new Error('newBox.saveFailed');
        newKey.current = generated.data;
      }
      const result = await santaClient.copyBox({
        ...toCreateInput({ ...draft, key: newKey.current }),
        sourceKey: item.box.key,
        adminUsername: draft.organizerName.trim(),
        createAdminCard: draft.participates === true,
        participants: draft.participantNames.map((username) => ({
          username,
          sourceCardId: originalCards.current.find(
            (card) =>
              !card.isYou &&
              normalizeQuickBoxName(card.username) === normalizeQuickBoxName(username),
          )?.id,
        })),
        fields: editedQuestions,
        ...(excludePreviousGiftees && canCopyExclusions ? { excludePreviousGiftees: true } : {}),
      });
      if (!result.ok || !result.data) throw new Error(result.error?.message || 'newBox.saveFailed');
      clearCopyGameDraft();
      trackGameCopy('game_copy_completed', source, 'account');
      closeDialog();
      navigate(premiumGameUrl(result.data.box));
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : 'newBox.saveFailed');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  if (error)
    return (
      <Stack gap={3}>
        <Hint tone="error">{t('boxes.duplicate.loadError')}</Hint>
        <Button variant="secondary" onClick={() => setAttempt((value) => value + 1)}>
          {t('boxes.duplicate.retry')}
        </Button>
      </Stack>
    );
  if (!draft) return <Spinner size="small" label={t('boxes.duplicate.loading')} />;

  return (
    <Stack gap={4}>
      <Typography as="p" role="meta/helper">
        {t('boxes.duplicate.source', { name: item.box.name })}
      </Typography>
      <fieldset className="duplicate-game__fields" disabled={busy} hidden={editingTheme}>
        <NewBoxReview
          premiumContext="copy_game"
          draft={draft}
          onChange={setDraft}
          onEditTheme={() => setEditingTheme(true)}
          onNamesProblem={setNamesProblem}
          onQuestionnaireBlocked={setBlocked}
          localQuestions={questions}
          onLocalQuestionsChange={setEditedQuestions}
          afterParticipants={
            <Stack gap={3}>
              <Switch
                hint={excludePreviousGiftees && canCopyExclusions ? t('boxes.duplicate.exclusionsHint') : undefined}
                checked={excludePreviousGiftees && canCopyExclusions}
                onCheckedChange={(enabled) => {
                  if (canCopyExclusions) setExcludePreviousGiftees(enabled);
                }}
                disabled={busy}
                label={<span className="duplicate-game__exclusions-label">
                  {t('boxes.duplicate.exclusionsToggle')}
                  {canCopyExclusions && <PremiumFeatureBadge feature="draw" />}
                </span>}
              />
              {!canCopyExclusions && (
                <PremiumUpsell feature="draw" compact context="copy_game" testId="copy-exclusions-upsell" />
              )}
            </Stack>
          }
        />
      </fieldset>
      {editingTheme && (
        <Stack gap={4}>
          <AppearancePicker
            theme={draft.theme}
            name={draft.name}
            picture={draft.picture}
            logo={draft.logo}
            disabled={uploading}
            showPreview={false}
            inDialog
            onThemeChange={(theme) => setDraft({ ...draft, theme })}
          >
            <PicturePicker
              value={draft.picture}
              logo={draft.logo}
              disabled={uploading}
              onValueChange={(picture) =>
                picture === 'custom_image'
                  ? upload.current?.open()
                  : setDraft({ ...draft, picture, logo: null })
              }
            />
          </AppearancePicker>
          <FileUpload
            ref={upload}
            kind="image"
            maxSize={IMAGE_UPLOAD_MAX_SIZE}
            onChange={(state, file) => {
              setUploading(state === 'selected');
              setUploadError(state === 'error');
              if (state === 'uploaded' && file)
                setDraft({ ...draft, picture: 'custom_image', logo: file.url });
            }}
          />
          {uploadError && <Hint tone="error">{t('upload.failed')}</Hint>}
          <Button disabled={uploading} onClick={() => setEditingTheme(false)}>
            {t('boxes.duplicate.backToReview')}
          </Button>
        </Stack>
      )}
      {namesProblem && <Hint tone="error">{t(`randomizer.problem.${namesProblem}`)}</Hint>}
      <Typography as="p" role="meta/helper">
        {t('boxes.duplicate.participants')} {t('boxes.duplicate.history')}
      </Typography>
      {saveError && <Hint tone="error">{t(saveError)}</Hint>}
      <div className="boxes-page__confirm-actions">
        <Button variant="secondary" disabled={busy} onClick={() => { clearCopyGameDraft(); closeDialog(); }}>
          {t('box.cancel')}
        </Button>
        <Button
          loading={busy}
          disabled={
            busy ||
            blocked ||
            Boolean(namesProblem) ||
            uploading ||
            editingTheme ||
            !draft.name.trim() ||
            draft.organizerName.trim().length < 2
          }
          onClick={() => void submit()}
        >
          {t('boxes.duplicate.submit')}
        </Button>
      </div>
    </Stack>
  );
}

/** Resume only the requesting account's draft after returning from Premium. */
export function ResumeCopyGame() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, status } = useAppSelector((state) => state.session);
  const handled = useRef<string | null>(null);
  useEffect(() => {
    const key = new URLSearchParams(location.search).get('resumeCopy');
    if (!key || status !== 'ready' || !user || handled.current === location.key) return;
    const saved = readCopyGameDraft(user.id, key);
    if (!saved) return;
    let cancelled = false;
    void santaClient.getBoxInfo({ key }).then((result) => {
      if (cancelled || !result.ok || !result.data?.isAdmin) return;
      handled.current = location.key;
      const params = new URLSearchParams(location.search);
      params.delete('resumeCopy');
      navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true, state: { ...location.state, resumedCopy: true } });
      showDialog({ title: t('boxes.duplicate.title'), mobileFullScreen: true, preventDismiss: true,
        hideClose: true, testId: 'duplicate-game-dialog',
        children: <DuplicateGameDialog item={result.data} source={saved.source} restored={saved} /> });
    }).catch(() => { /* Keep the saved draft available for a retry. */ });
    return () => { cancelled = true; };
  }, [location, user, status, navigate, t]);
  return null;
}
